import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FileText, ChevronRight, Plus, MessageCircle, ExternalLink } from "lucide-react";
import { useLenis } from "@/hooks/useLenis";
import { useSeo } from "@/lib/seo";
import { graph, webPageLd } from "@/lib/schema";
import { POLICIES } from "@/lib/policies";
import { Footer } from "@/components/site/Footer";
import { FloatingWhatsApp } from "@/components/site/FloatingWhatsApp";
import { Breadcrumbs, breadcrumbJsonLd } from "@/components/site/Breadcrumbs";

const useIsMobile = () => {
  const [m, setM] = useState(() => window.matchMedia("(max-width: 1023px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1023px)");
    const fn = (e) => setM(e.matches);
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);
  return m;
};

const SectionBody = ({ s }) => (
  <div className="text-[#5D4037] leading-[1.8] space-y-4">
    {s.paragraphs?.map((p, i) => <p key={i}>{p}</p>)}
    {s.bullets && (
      <ul className="grid gap-2.5 sm:grid-cols-1">
        {s.bullets.map((b) => (
          <li key={b} className="flex items-start gap-3">
            <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-[#D4A017] shrink-0" />
            <span>{b}</span>
          </li>
        ))}
      </ul>
    )}
    {s.note && <p className="p-4 rounded-xl bg-[#2E7D32]/[0.06] border border-[#2E7D32]/15 text-[#1F5D25] text-[15px]">{s.note}</p>}
  </div>
);

export default function PolicyPage({ policy }) {
  useLenis();
  const isMobile = useIsMobile();
  const crumbs = [{ label: "Home", to: "/" }, { label: policy.title }];
  const others = POLICIES.filter((p) => p.slug !== policy.slug);

  useSeo({
    title: policy.seoTitle,
    description: policy.description,
    path: policy.slug,
    jsonLd: graph(webPageLd({ path: policy.slug, name: policy.title, description: policy.description }), breadcrumbJsonLd(crumbs)),
  });

  return (
    <>
      <main data-testid={`policy-page${policy.slug.replace(/\//g, "-")}`}>
        <section className="relative pt-28 sm:pt-32 lg:pt-36 pb-12 lg:pb-16 bg-hero-gradient grain overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 relative">
            <Breadcrumbs items={crumbs} className="mb-6" />
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="chip chip-gold mb-5"><FileText className="w-3.5 h-3.5" /> {policy.kicker}</motion.div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-[#143C18] leading-[1.05] tracking-tight" data-testid="policy-title">
              <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
                <motion.span className="block" initial={{ y: "112%" }} animate={{ y: 0 }} transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}>{policy.title}</motion.span>
              </span>
            </h1>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5, duration: 0.7 }} className="mt-4 text-xs sm:text-sm uppercase tracking-[0.18em] text-[#8D6E63] font-semibold" data-testid="policy-updated">{policy.updated}</motion.p>
            <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.55 }} className="mt-6 text-base sm:text-lg text-[#5D4037] max-w-3xl leading-relaxed">{policy.intro}</motion.p>
          </div>
        </section>

        <section className="py-12 sm:py-16 bg-[#FFF8E7]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 grid lg:grid-cols-12 gap-10">
            <aside className="hidden lg:block lg:col-span-4">
              <div className="sticky top-28 space-y-6">
                <nav aria-label="On this page" className="card-premium !transform-none p-6" data-testid="policy-toc">
                  <div className="text-[11px] uppercase tracking-[0.25em] text-[#8D6E63] font-bold mb-4">On this page</div>
                  <ol className="space-y-2">
                    {policy.sections.map((s) => (
                      <li key={s.id}><a href={`#${s.id}`} className="text-sm text-[#1A1614] hover:text-[#2E7D32] transition-colors inline-flex items-start gap-2"><ChevronRight className="w-3.5 h-3.5 mt-1 text-[#D4A017] shrink-0" />{s.heading}</a></li>
                    ))}
                  </ol>
                </nav>
                <div className="p-6 rounded-3xl bg-[#143C18] text-white">
                  <div className="text-[11px] uppercase tracking-[0.25em] text-[#E8BE4A] font-bold mb-3">Other policies</div>
                  <ul className="space-y-2 text-sm">
                    {others.map((p) => <li key={p.slug}><Link to={p.slug} className="text-white/80 hover:text-white">{p.title}</Link></li>)}
                    <li><Link to="/contact-us" className="text-white/80 hover:text-white">Contact Us</Link></li>
                  </ul>
                </div>
              </div>
            </aside>

            <div className="lg:col-span-8 space-y-4 sm:space-y-5" data-testid="policy-sections">
              {policy.sections.map((s, i) => isMobile ? (
                <details key={s.id} id={s.id} open={i === 0} className="group rounded-2xl bg-white border border-[#8D6E63]/15 open:border-[#2E7D32]/40 open:shadow-lg transition-all overflow-hidden scroll-mt-24" data-testid={`policy-section-${s.id}`}>
                  <summary className="flex items-center justify-between gap-4 p-5 cursor-pointer">
                    <h2 className="font-display text-lg font-semibold text-[#143C18]">{s.heading}</h2>
                    <Plus className="acc-icon w-5 h-5 text-[#2E7D32] transition-transform shrink-0" />
                  </summary>
                  <div className="px-5 pb-6"><SectionBody s={s} /></div>
                </details>
              ) : (
                <motion.section
                  key={s.id}
                  id={s.id}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.6 }}
                  className="card-premium !transform-none p-7 lg:p-9 scroll-mt-28"
                  data-testid={`policy-section-${s.id}`}
                >
                  <h2 className="font-display text-2xl font-bold text-[#143C18] mb-4">{s.heading}</h2>
                  <SectionBody s={s} />
                </motion.section>
              ))}

              {policy.sourceUrl && (
                <p className="text-xs text-[#8D6E63] flex items-center gap-1.5">
                  <ExternalLink className="w-3.5 h-3.5" /> This policy mirrors the policy published in our Shopify store.{" "}
                  <a href={policy.sourceUrl} target="_blank" rel="noopener noreferrer" className="underline hover:text-[#2E7D32]">View on Shopify</a>
                </p>
              )}

              <div className="mt-6 p-7 sm:p-9 rounded-3xl bg-forest-deep text-white flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6" data-testid="policy-cta">
                <div>
                  <h2 className="font-display text-2xl sm:text-3xl font-bold">Need help with an order?</h2>
                  <p className="mt-2 text-white/80 text-sm sm:text-base">Share your Order ID and our team will sort it out.</p>
                </div>
                <div className="flex flex-wrap gap-3 shrink-0">
                  <Link to={policy.cta?.to || "/contact-us"} data-testid="policy-contact-support-btn" className="btn-gold !py-3">{policy.cta?.label || "Contact Us"} <ChevronRight className="w-4 h-4" /></Link>
                  <a href="https://wa.me/919889573628" target="_blank" rel="noopener noreferrer" className="btn-outline !border-white !text-white hover:!bg-white hover:!text-[#143C18] !py-3"><MessageCircle className="w-4 h-4" /> WhatsApp</a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer bottomPad="pb-10" />
      <FloatingWhatsApp />
    </>
  );
}
